// =============================================================================
// CorpusArchivePanel (W3 remove-an-import) — the corpus-management surface his
// live test asked for: "Need a way to delete import if it's the wrong file like
// this one." Two halves:
//
//   1. REMOVE — pick the wrong roles/achievements (checkboxes) → "Archive
//      selected". This is the provenance-FREE path that cleans HIS existing
//      wrong import (its rows predate the import_id back-link, so the ONLY
//      authority is his selection — the system never guesses which rows are
//      "wrong"; corrections are user-driven).
//   2. ARCHIVED (collapsed, greyed) — every archived role/card, each with Undo.
//      Nothing is ever hard-deleted; an archive is always reversible.
//
// Self-contained: reads the same roles/achievements the canvas already has,
// calls the archive/unarchive client (api.ts), and asks the parent to re-fetch
// (onChanged) so the canvas + this panel both reflect the new truth. Pure
// presentation over served data — no invented state.
// =============================================================================

import { useMemo, useState } from 'react';
import {
  type AchievementRecord,
  type RoleRecord,
  archiveCorpusSelection,
  unarchiveCorpusSelection,
} from '../lib/api';
import { activeRoles, archivedCards, archivedRoles } from '../lib/corpusGrouping';
import styles from './CorpusArchivePanel.module.css';

export interface CorpusArchivePanelProps {
  roles: RoleRecord[];
  achievements: AchievementRecord[];
  /** Re-fetch the corpus after an archive/unarchive so the canvas + this panel
   *  both show the new truth (the server is the source of truth, not local state). */
  onChanged: () => void | Promise<void>;
}

/** Live (non-archived) achievements — the ones eligible to be removed. Mirrors
 *  the canvas's own live filter (merged + archived both out). */
function liveAchievements(achievements: AchievementRecord[]): AchievementRecord[] {
  return achievements.filter((a) => a.status !== 'merged' && a.status !== 'archived');
}

export function CorpusArchivePanel({ roles, achievements, onChanged }: CorpusArchivePanelProps) {
  const [selectedRoleIds, setSelectedRoleIds] = useState<Set<string>>(new Set());
  const [selectedAchievementIds, setSelectedAchievementIds] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const liveRoles = useMemo(() => activeRoles(roles), [roles]);
  const liveCards = useMemo(() => liveAchievements(achievements), [achievements]);
  const removedRoles = useMemo(() => archivedRoles(roles), [roles]);
  const removedCards = useMemo(() => archivedCards(achievements), [achievements]);

  const roleLabel = (r: RoleRecord) => `${r.company} — ${r.title}`;
  const cardRoleName = (a: AchievementRecord) =>
    roles.find((r) => r.id === a.roleId)?.company ?? 'Unattached';

  const selectedCount = selectedRoleIds.size + selectedAchievementIds.size;

  const toggleRole = (id: string) =>
    setSelectedRoleIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAchievement = (id: string) =>
    setSelectedAchievementIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const clearSelection = () => {
    setSelectedRoleIds(new Set());
    setSelectedAchievementIds(new Set());
  };

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong — try again.');
    } finally {
      setBusy(false);
    }
  }

  const archiveSelected = () =>
    run(async () => {
      await archiveCorpusSelection({
        roleIds: [...selectedRoleIds],
        achievementIds: [...selectedAchievementIds],
      });
      clearSelection();
    });

  const undoRole = (id: string) => run(() => unarchiveCorpusSelection({ roleIds: [id] }));
  const undoCard = (id: string) => run(() => unarchiveCorpusSelection({ achievementIds: [id] }));

  const nothingToManage =
    liveRoles.length === 0 &&
    liveCards.length === 0 &&
    removedRoles.length === 0 &&
    removedCards.length === 0;
  if (nothingToManage) return null;

  const archivedTotal = removedRoles.length + removedCards.length;

  return (
    <section className={styles.panel} aria-label="Remove imported items">
      <header className={styles.head}>
        <h3 className={styles.title}>Remove imported items</h3>
        <p className={styles.sub}>
          Imported the wrong file? Tick the roles or cards that don't belong and archive them.
          Nothing is deleted — you can bring anything back below.
        </p>
      </header>

      {error && (
        <div className={styles.error} role="alert">
          {error}
        </div>
      )}

      {(liveRoles.length > 0 || liveCards.length > 0) && (
        <div className={styles.pickBlock}>
          {liveRoles.length > 0 && (
            <div className={styles.group}>
              <div className={styles.groupLabel}>Roles</div>
              {liveRoles.map((r) => (
                <label key={r.id} className={styles.row}>
                  <input
                    type="checkbox"
                    checked={selectedRoleIds.has(r.id)}
                    onChange={() => toggleRole(r.id)}
                    disabled={busy}
                  />
                  <span className={styles.rowText}>{roleLabel(r)}</span>
                </label>
              ))}
            </div>
          )}

          {liveCards.length > 0 && (
            <div className={styles.group}>
              <div className={styles.groupLabel}>Cards</div>
              {liveCards.map((a) => (
                <label key={a.id} className={styles.row}>
                  <input
                    type="checkbox"
                    checked={selectedAchievementIds.has(a.id)}
                    onChange={() => toggleAchievement(a.id)}
                    disabled={busy}
                  />
                  <span className={styles.rowText}>
                    {a.headline}
                    <span className={styles.rowMeta}> · {cardRoleName(a)}</span>
                  </span>
                </label>
              ))}
            </div>
          )}

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.archiveBtn}
              onClick={archiveSelected}
              disabled={busy || selectedCount === 0}
            >
              {busy ? 'Working…' : `Archive selected${selectedCount ? ` (${selectedCount})` : ''}`}
            </button>
            {selectedCount > 0 && !busy && (
              <button type="button" className={styles.clearBtn} onClick={clearSelection}>
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {archivedTotal > 0 && (
        <div className={styles.archivedBlock}>
          <button
            type="button"
            className={styles.archivedToggle}
            onClick={() => setShowArchived((v) => !v)}
            aria-expanded={showArchived}
          >
            {showArchived ? '▾' : '▸'} Archived ({archivedTotal})
          </button>

          {showArchived && (
            <div className={styles.archivedList}>
              {removedRoles.map((r) => (
                <div key={r.id} className={styles.archivedRow}>
                  <span className={styles.archivedText}>{roleLabel(r)}</span>
                  <button
                    type="button"
                    className={styles.undoBtn}
                    onClick={() => undoRole(r.id)}
                    disabled={busy}
                  >
                    Undo
                  </button>
                </div>
              ))}
              {removedCards.map((a) => (
                <div key={a.id} className={styles.archivedRow}>
                  <span className={styles.archivedText}>
                    {a.headline}
                    <span className={styles.rowMeta}> · {cardRoleName(a)}</span>
                  </span>
                  <button
                    type="button"
                    className={styles.undoBtn}
                    onClick={() => undoCard(a.id)}
                    disabled={busy}
                  >
                    Undo
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
